import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const COLORS = { black: '#0A0A0A', white: '#F5F5F0', gold: '#C9A84C', gray: '#888' };

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError('Email and password are required.');
      return;
    }

    setLoading(true);

    // Mock mode: accept any credentials and navigate
    if (MOCK_AUTH) {
      navigate(redirectTo, { replace: true });
      return;
    }

    if (!API_BASE_URL) {
      setError('API not configured. Set VITE_API_BASE_URL or enable VITE_MOCK_AUTH=true.');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
        credentials: 'include',
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || 'Invalid email or password.');
        setLoading(false);
        return;
      }

      navigate(redirectTo, { replace: true });
    } catch {
      setError('Network error. Check your connection and try again.');
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: COLORS.black,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0A0A0A; }
      `}</style>

      <div style={{
        width: '100%',
        maxWidth: '420px',
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
      }}>
        {/* Wordmark */}
        <div style={{ textAlign: 'center' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 900,
            letterSpacing: '0.15em',
            color: COLORS.white,
            margin: '0 0 0.5rem',
          }}>
            QRQY
          </h1>
          <p style={{
            fontSize: '0.75rem',
            color: COLORS.gray,
            letterSpacing: '0.12em',
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
          }}>
            Your account activates your piece and unlocks your QR.
          </p>
        </div>

        {/* Login card */}
        <div style={{
          border: `1px solid rgba(255,255,255,0.1)`,
          padding: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        }}>
          <h2 style={{
            fontSize: '0.8rem',
            fontWeight: 700,
            letterSpacing: '0.2em',
            color: COLORS.white,
            margin: 0,
          }}>
            SIGN IN
          </h2>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ fontSize: '0.65rem', letterSpacing: '0.15em', color: COLORS.gray, fontWeight: 600 }}>
                EMAIL
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="email"
                style={{
                  padding: '0.85rem 1rem',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: `1px solid rgba(255,255,255,0.12)`,
                  color: COLORS.white,
                  fontSize: '0.88rem',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <label style={{ fontSize: '0.65rem', letterSpacing: '0.15em', color: COLORS.gray, fontWeight: 600 }}>
                PASSWORD
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                style={{
                  padding: '0.85rem 1rem',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: `1px solid rgba(255,255,255,0.12)`,
                  color: COLORS.white,
                  fontSize: '0.88rem',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  outline: 'none',
                }}
              />
            </div>

            {error && (
              <p style={{
                fontSize: '0.78rem',
                color: '#e05c5c',
                margin: 0,
                padding: '0.75rem',
                backgroundColor: 'rgba(224,92,92,0.08)',
                border: '1px solid rgba(224,92,92,0.2)',
              }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.95rem',
                backgroundColor: COLORS.gold,
                color: COLORS.black,
                border: 'none',
                fontSize: '0.78rem',
                fontWeight: 800,
                letterSpacing: '0.2em',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                marginTop: '0.5rem',
              }}
            >
              {loading ? 'SIGNING IN...' : 'SIGN IN'}
            </button>
          </form>
        </div>

        {/* Signup link */}
        <p style={{
          textAlign: 'center',
          fontSize: '0.78rem',
          color: COLORS.gray,
          lineHeight: 1.6,
        }}>
          New here?{' '}
          <Link to="/signup" style={{ color: COLORS.white, textDecoration: 'underline', textUnderlineOffset: '3px' }}>
            Create your account
          </Link>
        </p>
      </div>
    </div>
  );
}
